import {type RequestOptions} from './redux-api';

import {captureSentryException} from 'src/utils/sentry';
import logger from './logger';


export class ApiError extends Error {
  options: RequestOptions;
  response: Response;
  responseBody: unknown;
  error: Error | null | undefined;
  fetchError: Error | null | undefined;

  constructor(
    options: RequestOptions,
    response: Response,
    responseBody?: unknown,
    error?: Error,
  ) {
    const {status} = response;
    const apiPath = response.headers.get('x-requested-route');

    let message = `ApiError - ${status}`;
    if (apiPath) {
      message += ` ${apiPath}`;
    }
    super(message);

    this.options = options;
    this.response = response;
    this.responseBody = responseBody;
    // TODO (kyle): change this to `fetchError`
    this.error = this.fetchError = error;
  }
}

/**
 * Returns the `message` string from an API error response body, if present.
 * Use for displaying API validation/error messages (e.g. in toasts).
 */
export function getApiErrorMessage(error: Error): string | undefined {
  if (error instanceof ApiError && error.responseBody != null) {
    const body = error.responseBody as {message?: string; error?: string};
    if (typeof body.message === 'string') {return body.message;}
    if (typeof body.error === 'string') {return body.error;}
  }
  return undefined;
}

export function captureApiError(
  error: ApiError,
  context?: Record<any, any>,
  extras?: Record<any, any>,
) {
  captureSentryException(error.error || error, {
    level: 'error',
    tags: {
      ...context,
      context: 'JS API',
      path: error.options.path,
      // this is set in the flask abort middleware and will return the generic route
      // for any request that is handled by an SoException
      apiPath: error.response.headers.get('x-requested-route'),
    },
    extra: {
      ...extras,
      response: error.responseBody,
    },
  });
}

/**
 * This function can be used to intelligently suppress errors
 * depending on who is at fault.
 */
export function handleGenericApiError(
  e: Error,
  ignoreStatus: Array<number> = [],
) {
  const error = wranglePossibleApiError(e);

  if (error instanceof ApiError) {
    const {status} = error.response;
    if (
      status < 500 &&
      status >= 300 &&
      status !== 401 &&
      !ignoreStatus.includes(status)
    ) {
      captureApiError(error);
    } else {
      logger.error(`ApiError (${status})`, error);
    }
  } else {
    captureSentryException(error, {
      level: 'error',
    });
  }
}

export function wranglePossibleApiError(
  error?: Record<any, any> | null,
): unknown {
  let result = error;
  // NOTE (kyle): utils/api.js returns a weird legacy error object.
  if (error?.apiError) {
    result = error.apiError;
  } else if (error?.error) {
    result = error.error;
  }
  return result;
}

// TODO (kyle): change this to NotFoundError and possibly inherit from
// ApiError.
export class NotFoundError extends Error {
  reason: string;
  status: number;

  constructor(reason: string) {
    super('NotFound: ' + reason);
    this.reason = reason;
    this.status = 404;
  }
}
// NOTE: (diwakersurya) this handles partial error for the api calls
// from bff
export class PartialError extends ApiError {}
